import { z } from 'zod';
import type { LocaleMap } from './geo.js';

/**
 * F12 venue-amenity types + Zod schemas.
 *
 * Decisions: see plans/12_F12_venue_profile_self_service.md (Decision 14).
 *
 * Amenities are a closed enum of 31 curated codes grouped by `category`. The
 * canonical list is seeded into [`venue_amenities_master`](packages/db/src/seed/venue-amenities.ts)
 * and joined via [`venue_amenities`](packages/db/src/schema.ts) (PK both).
 *
 * Storing as a closed enum (vs a free-form JSONB array) makes F14's filter
 * bottom sheet trivial (`WHERE amenity_code IN (...)`), keeps i18n labels
 * clean (one shared `amenities` namespace), and lets the editor render each
 * amenity with its `icon_code` from `@bravemove/design-tokens/icons`.
 *
 * Adding a new amenity post-MVP is a 3-line migration (one master row insert
 * + one i18n string + one Zod literal in this file).
 *
 * Note on naming: `code` is a DB-safe slug (e.g. `cafe` — no accents), while
 * `names.en` is the display label (which may be "Café" with accent).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const VENUE_AMENITY_CATEGORIES = [
  'access',
  'facility',
  'equipment',
  'court_surface',
  'service',
] as const;
export type VenueAmenityCategory = (typeof VENUE_AMENITY_CATEGORIES)[number];

/**
 * The 31 seeded amenity codes. Kept in sync with the rows in
 * [`packages/db/src/seed/venue-amenities.ts`](packages/db/src/seed/venue-amenities.ts);
 * the schema test ensures the two stay aligned (a drift would fail loudly).
 *
 * Grouped here by category for human readability; the canonical sort order
 * lives in the seed file's `sort_order` column.
 */
export const VENUE_AMENITY_CODES = [
  // access (100–199)
  'parking',
  'free_parking',
  'accessible_wheelchair',
  'kids_friendly',
  'womens_only_hours',
  // facility (200–299)
  'wifi',
  'showers',
  'lockers',
  'restrooms',
  'viewing_area',
  'ac',
  'heating',
  'indoor_lighting',
  'outdoor_floodlights',
  'sauna',
  'pool',
  // equipment (300–399)
  'equipment_rental',
  'racket_rental',
  'ball_rental',
  'towel_service',
  'water_dispenser',
  'pro_shop',
  // court_surface (400–499)
  'court_surface_grass',
  'court_surface_clay',
  'court_surface_acrylic',
  'court_surface_synthetic',
  'court_surface_carpet',
  // service (500–599)
  'cafe',
  'restaurant',
  'juice_bar',
  'kids_classes',
] as const;
export type VenueAmenityCode = (typeof VENUE_AMENITY_CODES)[number];

// ─── Raw DB row shapes ───────────────────────────────────────────────────────

export type VenueAmenityMaster = {
  readonly code: VenueAmenityCode;
  readonly names: LocaleMap;
  readonly category: VenueAmenityCategory;
  readonly iconCode: string;
  readonly sortOrder: number;
};

export type VenueAmenity = {
  readonly venueId: string;
  readonly amenityCode: VenueAmenityCode;
  readonly countryCode: string;
  readonly createdAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const VenueAmenityCategorySchema = z.enum(VENUE_AMENITY_CATEGORIES);
export const VenueAmenityCodeSchema = z.enum(VENUE_AMENITY_CODES);

/**
 * Full replace payload. PUT-replaces the venue's amenity rows wholesale.
 * Duplicates rejected by the .refine; the empty array clears all amenities.
 */
export const ReplaceVenueAmenitiesInputSchema = z
  .object({
    codes: z
      .array(VenueAmenityCodeSchema)
      .max(VENUE_AMENITY_CODES.length)
      .refine((arr) => new Set(arr).size === arr.length, {
        message: 'duplicate amenity codes are not allowed',
      }),
  })
  .strict();
export type ReplaceVenueAmenitiesInput = z.infer<typeof ReplaceVenueAmenitiesInputSchema>;
